/**
 * InboundMessagesRoute — 수신 메시지(포워더가 쌓은 문자·카톡·텔레그램) 화면 전용 가드.
 *
 * 전역 관리자(매장 무소속 admin/superadmin)와 "라인업 매장(admin-demo) 대표(owner)"만 통과시킨다.
 * lineup 계정은 role=owner 라 AdminRoute(admin 전용)로는 막혀서, 수신 메시지를 직접 챙기는
 * 라인업 대표를 위해 별도 가드를 둔다. 다른 매장 owner 가 새지 않게 tenantId 를 admin-demo 로 한정한다.
 * (판정은 AuthContext.hasHqDelegation 하나로 모았다 — 백엔드 hasHqDelegation 과 같은 선.)
 *
 * 로딩 처리(profile 일시 장애 → 스피너 후 복구 안내)는 SalesLeadRoute / GlobalAdminRoute 와 동일.
 */

import { useEffect, useState } from "react";
import { Navigate, Outlet } from "react-router";
import { useAuth } from "../../context/AuthContext";

/** 라인업 매장 부트스트랩 id — **권한 판정용이 아니다.**
 *  수신 문자 토스트가 "어느 매장 문자함을 볼까"의 기본값으로만 쓴다(데이터 스코프).
 *  권한은 hasHqDelegation 이 정한다. */
export const LINEUP_TENANT_ID = "admin-demo";
export const LINEUP_OWNER_EMAIL = "lineup@phoneboard.kr";

function Spinner() {
  return (
    <div className="flex h-screen items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-t-transparent" />
    </div>
  );
}

function StalledNotice() {
  return (
    <div className="flex h-screen flex-col items-center justify-center gap-4 px-4 text-center">
      <p className="text-sm text-gray-500 dark:text-gray-400">
        서버 응답이 지연되고 있어요. 잠시 후 다시 시도해주세요.
      </p>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-brand-600"
      >
        새로고침
      </button>
    </div>
  );
}

export default function InboundMessagesRoute() {
  const { user, profile, loading, profileLoading, hasHqDelegation } = useAuth();

  // user는 있는데 profile이 12초 넘게 null이면 일시 장애로 보고 복구 안내를 띄운다.
  const [stalled, setStalled] = useState(false);
  useEffect(() => {
    if (!loading && !profileLoading && user && profile === null) {
      const t = setTimeout(() => setStalled(true), 12000);
      return () => clearTimeout(t);
    }
    setStalled(false);
  }, [loading, profileLoading, user, profile]);

  if (loading || profileLoading) return <Spinner />;

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (profile === null) return stalled ? <StalledNotice /> : <Spinner />;

  // 본사 위임(전역 관리자 또는 hq_shared 쓰기)이면 통과 — 백엔드 가드와 같은 판정.
  const allowed = hasHqDelegation();
  if (!allowed) {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
}
